/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import BackIcon from "../images/back.png";
import { Button } from "src/components/ui/button";
import { cva } from "class-variance-authority";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { initiatePayment, verifyPayment } from "src/api/payments";
import Monnify from "monnify-js";
import env from "src/config/env";
import { getInvoiceDetails, sendInvoiceReminder } from "src/api/invoice";
import { getCustomizationValue, renderCurrency, renderNumber, showSuccessToast } from "src/lib/utils";
import CustomDataTable from "src/components/CustomDataTable";
import { useAuth } from "src/providers/AuthProvider";

const monnify = new Monnify(env.REACT_APP_MONNIFY_API_KEY, env.REACT_APP_MONNIFY_CONTRACT_CODE);

const statusVariants = cva("tw-capitalize tw-text-white tw-font-semibold !tw-px-4 !tw-py-2.5 tw-rounded-md", {
	variants: {
		status: {
			pending: "tw-bg-orange",
			partial: "tw-bg-orange",
			paid: "tw-bg-green",
		},
	},
	defaultVariants: {
		status: "default",
	},
});

const columns = [
	{ name: "Item", selector: (row) => row.product.name },
	{ name: "Qty", selector: (row) => renderNumber(row.qty) },
	{ name: "Unit Price", selector: (row) => renderCurrency(row.product_price) },
	{ name: "Total", selector: (row) => renderCurrency(row.amount) },
];

const InvoiceDetail = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { user } = useAuth();
	const { uuid } = useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const { data, refetch } = useQuery({
		queryKey: ["invoice", uuid],
		queryFn: () => getInvoiceDetails(uuid),
		suspense: true,
	});

	const { mutate: verifyMutate } = useMutation(verifyPayment, {
		onSuccess: () => refetch(),
	});
	const { mutate: initiateMutate } = useMutation(initiatePayment, {
		onSuccess: (data) => {
			monnify.initializePayment({
				...data,
				onClose: (response) => {
					if (response.status === "SUCCESS") {
						setSearchParams((params) => {
							params.delete("initiate");
							return params;
						});
						refetch();
						verifyMutate({ reference: response.paymentReference, business_uuid: user?.business_uuid });
					}
				},
			});
		},
	});
	const { mutate: sendReminder, isLoading: isReminderSending } = useMutation(sendInvoiceReminder, {
		onSuccess: () => {
			showSuccessToast("Reminder sent successfully");
			queryClient.invalidateQueries(["inventory"]);
		},
	});

	const invoice = data?.data;
	const shouldInitialisePayment = searchParams.has("initiate");

	useEffect(() => {
		if (invoice?.status !== "paid" && invoice?.payment_method === "transfer" && shouldInitialisePayment) {
			initiateMutate({ customer_id: invoice?.customer_id, invoice_id: invoice?.id, business_uuid: user?.business_uuid });
		}
	}, [initiateMutate, invoice?.customer_id, invoice?.id, invoice?.payment_method, invoice?.status, shouldInitialisePayment, user?.business_uuid]);

	return (
		<div className="pageWrapper">
			<div className="mainContent">
				<div className="pageTop">
					<button className="mr-1 transparent-btn" onClick={() => navigate("../..", { relative: "path" })}>
						<img src={BackIcon} />
					</button>
					<h2>{getCustomizationValue(user, "invoice_label", true)} Details</h2>
				</div>

				<div className="innerContent">
					{invoice && (
						<div className="inv_detail_wrap">
							<div className="inv_det_top">
								<h3>Billed to</h3>

								<div className="tw-flex tw-gap-3 tw-items-center tw-flex-wrap">
									<span className={statusVariants({ status: invoice?.status })}>Status: {invoice?.status}</span>
									{invoice?.status !== "paid" && (
										<>
											<CustomConfirmDialog
												trigger={
													<Button className="!tw-px-4 !tw-py-2.5" disabled={isReminderSending} variant="secondary">
														Send reminder
													</Button>
												}
												mutateFn={() => sendReminder({ uuid })}
											/>
											{invoice?.payment_method === "transfer" && (
												<CustomConfirmDialog
													trigger={<Button className="!tw-px-4 !tw-py-2.5">Initiate payment</Button>}
													mutateFn={() =>
														initiateMutate({
															customer_id: invoice?.customer_id,
															invoice_id: invoice?.id,
															business_uuid: user?.business_uuid,
														})
													}
												/>
											)}
										</>
									)}
								</div>
							</div>

							<div className="inv_det_sect">
								<div className="inv_det_top_left">
									<p>
										{getCustomizationValue(user, "customer_label", true)} Name:{" "}
										<strong>
											{invoice?.customer.first_name} {invoice?.customer.last_name}
										</strong>
									</p>
									<p>
										{getCustomizationValue(user, "customer_label", true)} Phone: <strong>{invoice?.customer.phone}</strong>
									</p>
									<p>
										{getCustomizationValue(user, "customer_label", true)} Email: <strong>{invoice?.customer.email}</strong>
									</p>
								</div>

								<div className="text-right inv_det_top_right">
									<p>
										Due Date: <strong>{format(new Date(invoice?.due_date), "MMM dd, yyyy")}</strong>
									</p>
									<p>
										{getCustomizationValue(user, "invoice_label", true)} Number: <strong>{invoice?.id + 1000000}</strong>
									</p>
								</div>
							</div>

							<div className="inv_det_sect">
								<div className="inv_det_top_left">
									<p>
										Payment Method: <strong className="tw-capitalize">{invoice?.payment_method}</strong>
									</p>

									{invoice?.payment_method === "transfer" && <p>Account Details: </p>}
								</div>

								<div className="inv_det_top_right"></div>
							</div>

							<div className="inv_det_content">
								<h3>Item Details</h3>
								<br />

								<CustomDataTable data={invoice?.items ?? []} columns={columns} pagination={false} />

								<div className="inv_totals">
									<div className="inv_total_itm">
										<p>Sub Total</p>
										<p>
											{new Intl.NumberFormat("en-NG", {
												style: "currency",
												currency: "NGN",
												minimumFractionDigits: 2,
											}).format(invoice?.amount)}
										</p>
									</div>

									<div className="inv_total_itm">
										<p>Discount</p>
										<p>
											{new Intl.NumberFormat("en-NG", {
												style: "currency",
												currency: "NGN",
												minimumFractionDigits: 2,
											}).format(invoice?.discount_amount)}
										</p>
									</div>

									<div className="inv_total_itm">
										<p>VAT (7.5%)</p>
										<p>
											{new Intl.NumberFormat("en-NG", {
												style: "currency",
												currency: "NGN",
												minimumFractionDigits: 2,
											}).format(invoice?.vat)}
										</p>
									</div>

									<div className="inv_total_itm">
										<p>
											<strong>{getCustomizationValue(user, "invoice_label", true)} Total</strong>
										</p>
										<p>
											<strong>
												{new Intl.NumberFormat("en-NG", {
													style: "currency",
													currency: "NGN",
													minimumFractionDigits: 2,
												}).format(invoice?.total_amount)}
											</strong>
										</p>
									</div>

									<div className="inv_total_itm">
										<p>
											<strong>Transaction Fee</strong>
										</p>
										<p>
											<strong>
												{new Intl.NumberFormat("en-NG", {
													style: "currency",
													currency: "NGN",
													minimumFractionDigits: 2,
												}).format(invoice?.transaction_fee)}
											</strong>
										</p>
									</div>

									<div className="inv_total_itm">
										<h4>Transaction Amount</h4>
										<h4>
											{new Intl.NumberFormat("en-NG", {
												style: "currency",
												currency: "NGN",
												minimumFractionDigits: 2,
											}).format(invoice?.transaction_amount)}
										</h4>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

// Reusable components

export default InvoiceDetail;
